import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { useProgram } from "../../context/ProgramContext";
import { supabase } from "../../supabaseClient";
import { useNavigate } from "react-router-dom";
import SCPurchaseButton from "../../components/SCPurchaseButton";
import { generateSCProgram } from "../../components/SCWorkoutGenerator";
import { User, Clock, Calendar, ChevronRight, Info, Dumbbell, X, Activity } from "lucide-react";

// --- Static Icon Definitions ---
const Icons = {
  User: () => <User size={18} className="text-gray-500" />,
  Clock: () => <Clock size={18} className="text-gray-500" />,
  Calendar: () => <Calendar size={18} className="text-gray-500" />,
  ChevronRight: () => <ChevronRight size={18} />,
  Info: () => <Info size={18} className="text-blue-500" />,
  Dumbbell: () => <Dumbbell size={20} className="text-gray-400" />,
  Close: () => <X size={20} className="text-gray-500" />,
  Activity: () => <Activity size={18} className="text-gray-500" />,
};

export default function SCWorkouts({ userId: propUserId }) {
  const navigate = useNavigate();
  const { selectedProgram, setSelectedProgram } = useProgram();

  // --- State ---
  const [currentUserId, setCurrentUserId] = useState(propUserId);
  const [programs, setPrograms] = useState([]);
  const [modeledPlayer, setModeledPlayer] = useState({ customLabel: "" });
  const [allSwots, setAllSwots] = useState([]);
  const [selectedSwotId, setSelectedSwotId] = useState("");
  const [numWorkouts, setNumWorkouts] = useState(4);
  const [workoutLength, setWorkoutLength] = useState(60);
  const [selectedTrainingDays, setSelectedTrainingDays] = useState([1, 3, 5, 6]); // Mon, Wed, Fri, Sat
  const [todaysWorkouts, setTodaysWorkouts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [hasProgramWorkoutAccess, setHasProgramWorkoutAccess] = useState(false);
  const [checkingProgramWorkoutAccess, setCheckingProgramWorkoutAccess] = useState(false);
  const [hasActiveSCMonthlyProgram, setHasActiveSCMonthlyProgram] = useState(false);
  const [activeSCMonthlyProgram, setActiveSCMonthlyProgram] = useState(null);
  const [generatingProgram, setGeneratingProgram] = useState(false);

  const dayOptions = [
    { value: 1, label: "Mon" },
    { value: 2, label: "Tue" },
    { value: 3, label: "Wed" },
    { value: 4, label: "Thu" },
    { value: 5, label: "Fri" },
    { value: 6, label: "Sat" },
    { value: 0, label: "Sun" },
  ];

  // --- 1. Robust User ID Resolution ---
  useEffect(() => {
    if (propUserId) {
      setCurrentUserId(propUserId);
    } else {
      supabase.auth.getSession().then(({ data }) => {
        if (data?.session?.user?.id) {
          setCurrentUserId(data.session.user.id);
        }
      });
    }
  }, [propUserId]);

  // --- 2. Fetch Programs ---
  useEffect(() => {
    if (!currentUserId) return;
    async function fetchPrograms() {
      const { data, error } = await supabase
        .from("user_program_follows")
        .select("program_id, programs(*)")
        .eq("user_id", currentUserId);

      if (error) console.error("Error fetching programs:", error);
      else setPrograms(data || []);
    }
    fetchPrograms();
  }, [currentUserId]);

  // --- 3. Set Default Program ---
  useEffect(() => {
    if (!selectedProgram && programs.length > 0 && setSelectedProgram) {
      const defaultProgram = programs[0];
      setSelectedProgram({
        ...(defaultProgram.programs || {}),
        id: defaultProgram.program_id,
      });
    }
  }, [programs, selectedProgram, setSelectedProgram]);

  useEffect(() => {
    const bounded = Math.max(1, Math.min(7, Number(numWorkouts) || 1));
    if (bounded !== Number(numWorkouts)) {
      setNumWorkouts(bounded);
      return;
    }

    setSelectedTrainingDays((prev) => {
      if (prev.length === bounded) return prev;
      if (prev.length > bounded) return prev.slice(0, bounded);
      const defaults = [1, 2, 3, 4, 5, 6, 0];
      const next = [...prev];
      for (const day of defaults) {
        if (!next.includes(day)) next.push(day);
        if (next.length === bounded) break;
      }
      return next;
    });
  }, [numWorkouts]);

  useEffect(() => {
    async function fetchProgramWorkoutAccess() {
      if (!currentUserId || !selectedProgram?.id) {
        setHasProgramWorkoutAccess(false);
        return;
      }

      setCheckingProgramWorkoutAccess(true);
      try {
        const today = dayjs().format("YYYY-MM-DD");
        const [
          { data: purchaseRows, error: purchaseError },
          { data: devRows, error: devError },
        ] = await Promise.all([
          supabase
            .from("program_purchases")
            .select("roster_limit, start_date, end_date")
            .eq("program_id", selectedProgram.id)
            .eq("status", "active")
            .lte("start_date", today)
            .gte("end_date", today)
            .order("end_date", { ascending: false })
            .limit(1),
          supabase
            .from("athlete_development_programs")
            .select("id, start_date, end_date, status")
            .eq("athlete_id", currentUserId)
            .eq("program_id", selectedProgram.id)
            .eq("status", "active")
            .lte("start_date", today)
            .gte("end_date", today)
            .limit(1),
        ]);

        if (purchaseError) throw purchaseError;
        if (devError) throw devError;

        let hasProgramPurchaseAccess = false;
        const rosterLimit = Number(purchaseRows?.[0]?.roster_limit || 0);
        if (rosterLimit > 0) {
          const { data: rosteredRows, error: rosterError } = await supabase
            .from("rostered_users")
            .select("user_id, created_at, teams!inner(program_id)")
            .eq("teams.program_id", selectedProgram.id)
            .order("created_at", { ascending: true })
            .limit(rosterLimit);

          if (rosterError) throw rosterError;
          hasProgramPurchaseAccess = (rosteredRows || []).some((row) => String(row.user_id) === String(currentUserId));
        }

        const hasActiveAthleteProgram = (devRows || []).length > 0;

        setHasProgramWorkoutAccess(hasProgramPurchaseAccess || hasActiveAthleteProgram);
      } catch (err) {
        console.error("Error checking SC workout access:", err);
        setHasProgramWorkoutAccess(false);
      } finally {
        setCheckingProgramWorkoutAccess(false);
      }
    }

    fetchProgramWorkoutAccess();
  }, [currentUserId, selectedProgram?.id]);

  useEffect(() => {
    async function fetchActiveSCMonthlyProgram() {
      if (!currentUserId || !selectedProgram?.id) {
        setHasActiveSCMonthlyProgram(false);
        setActiveSCMonthlyProgram(null);
        return;
      }

      const today = dayjs().format("YYYY-MM-DD");

      try {
        const { data, error } = await supabase
          .from("user_exercise_programs")
          .select("id, start_date, end_date")
          .eq("athlete_id", currentUserId)
          .eq("followed_program", selectedProgram.id)
          .lte("start_date", today)
          .gte("end_date", today)
          .order("start_date", { ascending: false })
          .limit(1);

        if (error) throw error;

        const active = data?.[0] || null;
        setActiveSCMonthlyProgram(active);
        setHasActiveSCMonthlyProgram(Boolean(active));
      } catch (err) {
        console.error("Error checking active S&C monthly program:", err);
        setHasActiveSCMonthlyProgram(false);
        setActiveSCMonthlyProgram(null);
      }
    }

    fetchActiveSCMonthlyProgram();
  }, [currentUserId, selectedProgram?.id]);

  // --- 4. Fetch SWOTs (program first, fallback to sport) ---
  useEffect(() => {
    async function fetchSwots() {
      if (!currentUserId) {
        setAllSwots([]);
        setSelectedSwotId("");
        return;
      }
      try {
        const baseQuery = () => supabase
          .from("athlete_swot")
          .select(`
            id,
            program_id,
            position_id,
            prebuilt_name,
            updated_at,
            favorited,
            programs:program_id (name, sport),
            position:positions!athlete_swot_position_id_fkey(name)
          `)
          .eq("athlete_id", currentUserId)
          .in("status", ["draft", "published"])
          .order("favorited", { ascending: false })
          .order("updated_at", { ascending: false });

        let data = [];
        let error = null;

        if (selectedProgram?.id) {
          const res = await baseQuery().eq("program_id", selectedProgram.id);
          data = res.data || [];
          error = res.error || null;
        }

        if (!error && data.length === 0 && selectedProgram?.sport) {
          const res = await baseQuery().eq("programs.sport", selectedProgram.sport);
          data = res.data || [];
          error = res.error || null;
        }

        if (error) throw error;

        setAllSwots(data || []);
        if ((data || []).length > 0) {
          const favorited = data.find((row) => row?.favorited);
          setSelectedSwotId(String((favorited || data[0]).id));
        } else {
          setSelectedSwotId("");
        }
      } catch (err) {
        console.error("Error fetching modeled player:", err);
        setAllSwots([]);
        setSelectedSwotId("");
      }
    }
    fetchSwots();
  }, [currentUserId, selectedProgram?.id, selectedProgram?.sport]);

  useEffect(() => {
    const selected = allSwots.find((row) => String(row.id) === String(selectedSwotId)) || null;
    setModeledPlayer({ customLabel: selected?.prebuilt_name || "No Athlete Found" });
  }, [allSwots, selectedSwotId]);

  const selectedProgramSport = selectedProgram?.sport?.trim().toLowerCase() || "";
  const swotsForSelectedSport = allSwots.filter((s) => {
    const sport = s.programs?.sport?.trim().toLowerCase() || "";
    return sport && sport === selectedProgramSport;
  });
  const swotsForSelectedProgram = swotsForSelectedSport.filter(
    (s) => String(s.program_id || "") === String(selectedProgram?.id || "")
  );

  useEffect(() => {
    const selectable = swotsForSelectedProgram.length > 0 ? swotsForSelectedProgram : swotsForSelectedSport;
    if (selectable.length === 0) {
      setSelectedSwotId("");
      return;
    }
    const currentStillValid = selectable.some((s) => String(s.id) === String(selectedSwotId));
    if (currentStillValid) return;
    const favorited = selectable.find((s) => s.favorited);
    setSelectedSwotId(String((favorited || selectable[0]).id));
  }, [selectedProgramSport, selectedSwotId, swotsForSelectedProgram, swotsForSelectedSport]);

  // --- 5. Fetch Today's Workouts (Reusable function) ---
  const fetchTodaysWorkouts = async () => {
    if (!currentUserId || !selectedProgram?.id) return;
    const today = dayjs().format("YYYY-MM-DD");
    const { data } = await supabase
      .from("daily_exercise_drills")
      .select("*")
      .eq("drill_date", today)
      .eq("user_id", currentUserId)
      .eq("followed_program", selectedProgram.id)
      .order("order_index", { ascending: true });
    
    setTodaysWorkouts(data || []);
  };

  useEffect(() => {
    fetchTodaysWorkouts();
  }, [currentUserId, selectedProgram]);

  // --- Handlers ---
  const goToWeeklyWorkouts = () => {
    if (selectedProgram?.id) {
      navigate("/weekly-workouts", { state: { programId: selectedProgram.id } });
    }
  };

  const handleProgramGenerated = () => {
    // Refresh the view immediately after purchase/generation
    fetchTodaysWorkouts();
    alert("Program successfully generated!");
  };

  const toggleTrainingDay = (dayValue) => {
    setSelectedTrainingDays((prev) => {
      const exists = prev.includes(dayValue);
      if (exists) return prev.filter((d) => d !== dayValue);
      if (prev.length >= Number(numWorkouts)) return prev;
      return [...prev, dayValue];
    });
  };

  const handleDirectProgramGenerate = async () => {
    if (!currentUserId || !selectedProgram?.id) return;
    setGeneratingProgram(true);
    try {
      await generateSCProgram({
        userId: currentUserId,
        program: selectedProgram,
        numWorkouts,
        trainingDays: selectedTrainingDays,
        workoutLength,
        modeledPlayer: athleteNameDisplay,
        supabase,
        setStatusMessage: () => {},
      });
      await fetchTodaysWorkouts();
      alert("Program successfully generated!");
    } catch (err) {
      console.error("Direct SC generation error:", err);
      alert("Failed to generate program.");
    } finally {
      setGeneratingProgram(false);
    }
  };

  // Helper to safely get the athlete name string
  const athleteNameDisplay = typeof modeledPlayer === 'object'
    ? modeledPlayer.customLabel
    : modeledPlayer;

  return (
    <div className="min-h-screen bg-gray-100 pb-20 font-sans">
      {/* HEADER */}
      <div className="bg-white px-6 py-4 shadow-sm sticky top-0 z-10">
        <h1 className="text-xl font-extrabold text-gray-900 text-center tracking-tight">
          Strength & Conditioning
        </h1>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-6">

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs font-bold text-blue-600 uppercase tracking-wider">Offseason Development</p>
            <p className="text-sm text-slate-600 mt-1">
              Buy a full development program for your selected team.
            </p>
          </div>
          <button
            onClick={() => selectedProgram?.id && navigate(`/athlete/offseason-program?programId=${selectedProgram.id}`)}
            disabled={!selectedProgram?.id}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-5 rounded-xl shadow transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Buy Offseason Program
          </button>
        </div>

        {/* SECTION 1: SETTINGS */}
        <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-200/50">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-1">
            Program Configuration
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* SWOT Selector */}
            <div className="bg-white rounded-xl p-3 flex items-center gap-3 border border-gray-100 shadow-sm">
              <div className="bg-gray-100 p-2 rounded-lg"><Icons.User /></div>
              <div className="flex-1">
                <label className="text-[10px] font-semibold text-gray-500 uppercase block">Select SWOT Analysis</label>
                <div className="relative">
                  <select
                    className="appearance-none w-full bg-white border border-gray-200 text-gray-900 text-sm font-semibold rounded-xl px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all disabled:bg-gray-50 disabled:text-gray-400"
                    value={selectedSwotId}
                    onChange={(e) => setSelectedSwotId(e.target.value)}
                    disabled={!selectedProgram || (swotsForSelectedProgram.length === 0 && swotsForSelectedSport.length === 0)}
                  >
                    {(swotsForSelectedProgram.length === 0 && swotsForSelectedSport.length === 0) ? (
                      <option value="">No SWOTs found for this sport</option>
                    ) : (
                      (swotsForSelectedProgram.length > 0 ? swotsForSelectedProgram : swotsForSelectedSport).map((swot) => (
                        <option key={swot.id} value={swot.id}>
                          {`${swot.programs?.name || "Program SWOT"}${swot.position?.name ? ` • ${swot.position.name}` : ""}`}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>
            </div>

            {/* Sessions */}
              <div className="bg-white rounded-xl p-3 flex items-center gap-3 border border-gray-100 shadow-sm">
                <div className="bg-gray-100 p-2 rounded-lg"><Icons.Activity /></div>
                <div className="flex-1">
                <label className="text-[10px] font-semibold text-gray-500 uppercase block">Sessions / Week</label>
                <input
                  type="number"
                  value={numWorkouts}
                  min={1}
                  max={7}
                  onChange={(e) => setNumWorkouts(Number(e.target.value))}
                  className="w-full text-gray-900 font-bold text-sm bg-transparent border-none p-0 focus:ring-0 focus:outline-none"
                />
              </div>
            </div>

            {/* Duration */}
            <div className="bg-white rounded-xl p-3 flex items-center gap-3 border border-gray-100 shadow-sm">
              <div className="bg-gray-100 p-2 rounded-lg"><Icons.Clock /></div>
              <div className="flex-1">
                <label className="text-[10px] font-semibold text-gray-500 uppercase block">Duration (Min)</label>
                <input
                  type="number"
                  value={workoutLength}
                  onChange={(e) => setWorkoutLength(Number(e.target.value))}
                  className="w-full text-gray-900 font-bold text-sm bg-transparent border-none p-0 focus:ring-0 focus:outline-none"
                />
              </div>
            </div>

            <div className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm md:col-span-3">
              <label className="text-[10px] font-semibold text-gray-500 uppercase block mb-2">
                Training Days ({selectedTrainingDays.length}/{numWorkouts})
              </label>
              <div className="flex flex-wrap gap-2">
                {dayOptions.map((day) => {
                  const active = selectedTrainingDays.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleTrainingDay(day.value)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition ${
                        active
                          ? "bg-blue-600 text-white border-blue-600"
                          : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
              {selectedTrainingDays.length !== Number(numWorkouts) && (
                <p className="text-xs text-amber-600 font-semibold mt-2">
                  Select exactly {numWorkouts} day{Number(numWorkouts) === 1 ? "" : "s"}.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* SECTION 2: GENERATION TOOLS */}
        <div className="bg-white rounded-3xl p-6 shadow-2xl shadow-blue-200/60 border border-blue-50 relative overflow-hidden max-w-xl mx-auto">
          
           {/* Decorative BG */}
           <div className="absolute top-0 right-0 -mt-10 -mr-10 text-blue-50 opacity-50 pointer-events-none">
            <svg className="w-48 h-48" fill="currentColor" viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
          </div>

          <div className="relative z-10 space-y-6">
            {/* Program Selector */}
            <div>
              <label className="text-sm font-bold text-gray-700 mb-2 block text-center">
                Select Active Program
              </label>
              <div className="relative">
                <select
                  className="appearance-none w-full bg-blue-50/50 border border-blue-100 text-gray-900 text-lg font-bold rounded-2xl px-6 py-4 text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all cursor-pointer"
                  value={selectedProgram?.id || ""}
                  onChange={(e) => {
                    const programId = e.target.value;
                    const program = programs.find((p) => p.program_id === programId);
                    if (program) {
                      setSelectedProgram({
                        ...(program.programs || {}),
                        id: program.program_id,
                      });
                    }
                  }}
                >
                  <option value="" disabled>-- Select Program --</option>
                  {programs.map((p) => (
                    <option key={p.program_id} value={p.program_id}>
                      {p.programs.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none text-blue-500">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7"></path></svg>
                </div>
              </div>
            </div>

            {/* --- ACTION BUTTONS --- */}
            <div className="space-y-3">
              {hasActiveSCMonthlyProgram ? (
                <div className="w-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-semibold py-3 rounded-xl text-center">
                  Monthly S&C plan active ({dayjs(activeSCMonthlyProgram?.start_date).format("MMM D")} - {dayjs(activeSCMonthlyProgram?.end_date).format("MMM D")}).
                </div>
              ) : checkingProgramWorkoutAccess ? (
                <div className="w-full bg-white border border-gray-200 text-gray-500 text-sm font-semibold py-3 rounded-xl text-center">
                  Checking workout access...
                </div>
              ) : hasProgramWorkoutAccess ? (
                <button
                  onClick={handleDirectProgramGenerate}
                  disabled={generatingProgram || selectedTrainingDays.length !== Number(numWorkouts)}
                  className="w-full bg-blue-600 text-white font-bold py-3 rounded-xl hover:bg-blue-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generatingProgram ? "Generating Program..." : "Generate S&C Program"}
                </button>
              ) : (
                <div className="w-full">
                  <SCPurchaseButton
                    user={currentUserId}
                    selectedProgram={selectedProgram}
                    numWorkouts={numWorkouts}
                    trainingDays={selectedTrainingDays}
                    workoutLength={workoutLength}
                    modeledPlayer={athleteNameDisplay}
                    onProgramGenerated={handleProgramGenerated}
                  />
                </div>
              )}

              {/* Monthly Overview Link */}
              {hasActiveSCMonthlyProgram && (
                <button
                  onClick={goToWeeklyWorkouts}
                  className="w-full bg-white border-2 border-blue-100 text-blue-600 font-bold py-3 rounded-xl hover:bg-blue-50 hover:border-blue-200 transition-all shadow-sm flex items-center justify-center gap-2"
                >
                  <Icons.Calendar />
                  View Full Monthly Calendar
                </button>
              )}
            </div>
          </div>
        </div>

        {/* SECTION 3: TODAY'S WORKOUT */}
        <div className="mt-8 space-y-4">
          <h2 className="text-lg font-bold text-gray-800 px-1">Today's Session</h2>

          {(!todaysWorkouts || todaysWorkouts.length === 0) ? (
            <div className="bg-white rounded-xl p-5 flex items-center justify-between border border-gray-100 shadow-sm">
              <div>
                <p className="text-gray-800 font-bold">No workouts scheduled today.</p>
                <p className="text-xs text-gray-400 mt-1">Generate a program above to get started.</p>
              </div>
              <div className="bg-gray-100 p-2 rounded-full"><Icons.Dumbbell /></div>
            </div>
          ) : (
            <div
              onClick={() => setShowModal(true)}
              className="group bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-6 shadow-lg shadow-blue-200 text-white cursor-pointer relative overflow-hidden transition-transform active:scale-[0.98]"
            >
              <div className="relative z-10">
                <div className="flex justify-between items-start mb-4">
                  <span className="inline-block bg-white/20 px-3 py-1 rounded-lg text-xs font-bold backdrop-blur-sm uppercase tracking-wider">
                    Today's Priority
                  </span>
                  <div className="bg-white/20 p-2 rounded-full backdrop-blur-sm group-hover:bg-white/30 transition-colors">
                    <Icons.ChevronRight />
                  </div>
                </div>

                <h3 className="text-2xl font-extrabold leading-tight mb-2">
                  {todaysWorkouts[0].drill_name}
                </h3>

                {todaysWorkouts[0].focus_areas?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {todaysWorkouts[0].focus_areas.map((area, idx) => (
                      <span key={idx} className="text-xs font-semibold text-blue-100 bg-blue-800/30 px-2 py-0.5 rounded">
                        #{area}
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center gap-2 text-blue-50 font-medium text-sm">
                  <Icons.Clock />
                  <span>Load: {todaysWorkouts[0].duration_or_reps || "See Details"}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 sm:p-0">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity" onClick={() => setShowModal(false)}></div>
          <div className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden relative z-10 flex flex-col max-h-[85vh] animate-in slide-in-from-bottom-full sm:zoom-in duration-300">
            
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-white sticky top-0 z-20">
              <div className="pr-4">
                <h2 className="text-lg font-bold text-gray-900 leading-tight">Today's Full Regimen</h2>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 shrink-0">
                <Icons.Close />
              </button>
            </div>

            <div className="p-6 overflow-y-auto bg-gray-50 space-y-4">
              {todaysWorkouts.map((w, idx) => (
                <div key={w.id || idx} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex gap-3">
                  <div className="bg-blue-100 text-blue-700 w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm shrink-0">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 text-lg mb-1">{w.drill_name}</h4>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium border border-blue-100">
                        {w.duration_or_reps || "N/A"}
                      </span>
                    </div>
                    {w.notes && (
                      <p className="text-sm text-gray-500 italic bg-gray-50 p-2 rounded border border-gray-100">
                        "{w.notes}"
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-gray-100 bg-white">
              <button onClick={() => setShowModal(false)} className="w-full py-3 bg-gray-900 text-white rounded-xl font-bold hover:bg-black transition-colors shadow-lg">
                Close View
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
